/**
 * 랜딩 전용 전역 스타일.
 *
 * ../../../../landing/index.html(v1.7)의 <style> 블록을 그대로 옮긴 것입니다.
 * * 원본이 Tailwind 없이 순수 CSS로 작성돼 있어 클래스를 변환하지 않고 유지했다.
 * 원본 파일이 갱신되면 이 블록만 통째로 교체하면 됩니다.
 *
 * ★ styled-jsx global 인 이유: 이 CSS 에는 `body`/`*` 리셋과 `.section`·`.stat` 같은
 * 흔한 클래스명이 들어 있어, styles/global.css 로 올리면 학습 화면 전체가 깨집니다.
 * styled-jsx 는 컴포넌트가 마운트된 동안에만 <style> 을 붙였다 떼므로,
 * 랜딩을 벗어나면(로그인 홈 등) 흔적 없이 사라집니다.
 */
import { color, scale } from "@/constants/palette";

export default function LandingStyles() {
  return (
    <style jsx global>{`
/* ─────────────────────────────────────────────────────────────
   해봄 랜딩. 디자인 토큰은 운영 중인 dq.ai.kr 화면에서 추출한 값입니다.
   ───────────────────────────────────────────────────────────── */
:root {
  /* 텍스트 */
  --ink: ${color.heading};
  --ink-2: ${color.body};
  --ink-3: ${color.subtle};
  /* 서피스 */
  --surface: ${color.white};
  --surface-2: ${color.page};
  --surface-3: ${color.fill};
  --line: ${color.line};
  /* 브랜드 */
  --brand: ${color.primaryInk};
  --brand-hover: ${color.primaryHover};
  --brand-tint: ${color.primaryMuted};
  --brand-tint-2: ${color.primarySoft};

  /* 좌우 여백과 본문 최대 폭 */
  --gutter: clamp(1.25rem, 4vw, 2.5rem);
  --shell: 1180px;
  /* 섹션 상하 여백은 뷰포트 "높이"에도 반응시켜, 세로가 짧은 노트북에서 덜 답답하게 */
  --section-y: clamp(3.5rem, 7vh + 1rem, 6rem);
  --header-h: 57px; /* dq.ai.kr 헤더와 동일: 로고 24px + 상하 패딩 16px */
}

*, *::before, *::after { box-sizing: border-box; }
* { margin: 0; min-width: 0; }

html {
  scroll-behavior: smooth;
  /* 노트북 화면 폭에 따라 전체 타이포 스케일을 미세 조정 */
  font-size: clamp(15px, 0.3vw + 13.7px, 16.5px);
}

body {
  background: var(--surface);
  color: var(--ink);
  font-family: "Pretendard", -apple-system, "Apple SD Gothic Neo", "Malgun Gothic", system-ui, sans-serif;
  letter-spacing: -0.01em;
  line-height: 1.5;
  -webkit-font-smoothing: antialiased;
  overflow-x: hidden;
}

img, svg { display: block; max-width: 100%; }
a { color: inherit; text-decoration: none; }
ul, ol { list-style: none; padding: 0; }
button, input { font: inherit; color: inherit; }
button { cursor: pointer; border: 0; background: none; }

::selection { background: var(--brand-tint); color: var(--brand); }

:focus-visible {
  outline: 2px solid var(--brand);
  outline-offset: 3px;
  border-radius: 4px;
}

.sr-only {
  position: absolute; width: 1px; height: 1px;
  padding: 0; overflow: hidden; clip: rect(0 0 0 0);
  white-space: nowrap; border: 0;
}
.skip-link:focus {
  position: fixed; top: .75rem; left: .75rem; z-index: 60;
  width: auto; height: auto; clip: auto;
  background: var(--ink); color: #fff;
  padding: .5rem 1rem; border-radius: 999px; font-size: .9rem;
}

/* ── 레이아웃 ────────────────────────────────────────────────── */
.shell {
  width: min(100% - var(--gutter) * 2, var(--shell));
  margin-inline: auto;
}
.section {
  padding-block: var(--section-y);
  scroll-margin-top: var(--header-h);
}

/* ── 타이포 (vw + vh 혼합: 노트북 세로 높이까지 고려) ───────────── */
.t-display {
  font-size: clamp(2rem, 1.4vw + 1.4vh + 0.9rem, 3.25rem);
  line-height: 1.25; letter-spacing: -0.035em; font-weight: 700;
  word-break: keep-all;
}
.t-h2 {
  font-size: clamp(1.5rem, 0.9vw + 0.9vh + 0.7rem, 2.25rem);
  line-height: 1.35; letter-spacing: -0.032em; font-weight: 700;
  word-break: keep-all;
}
.t-h3 {
  font-size: clamp(1.05rem, 0.4vw + 0.88rem, 1.25rem);
  line-height: 1.45; letter-spacing: -0.025em; font-weight: 700;
}
.t-body {
  font-size: clamp(0.94rem, 0.22vw + 0.87rem, 1.03rem);
  line-height: 1.72; color: var(--ink-2); word-break: keep-all;
}
/* 섹션 머리표: 브랜드 틴트 배경의 작은 알약 */
.eyebrow {
  display: inline-block; border-radius: 999px;
  background: var(--brand-tint); color: var(--brand);
  padding: .35rem .8rem; font-size: .8rem; font-weight: 600;
}

/* ── 버튼 ────────────────────────────────────────────────────── */
.btn-primary {
  display: inline-flex; align-items: center; justify-content: center; gap: .6rem;
  background: var(--brand); color: #fff;
  font-weight: 700; font-size: .98rem;
  border-radius: 999px; padding: .9rem 1.75rem;
  transition: background-color .16s ease;
}
.btn-primary:hover { background: var(--brand-hover); }
.btn-primary svg { transition: transform .16s ease; }
.btn-primary:hover svg { transform: translateY(2px); }

/* ── 헤더 (v1.8: dq.ai.kr 헤더 레이아웃과 동일 · 다크 히어로 위에서만 투명) ── */
.site-header {
  position: fixed; top: 0; left: 0; right: 0; z-index: 40;
  height: var(--header-h);
  border-bottom: 1px solid transparent;
  background: transparent;
  transition: background-color .25s ease, border-color .25s ease;
}
.site-header.scrolled {
  border-bottom-color: var(--line); /* dq.ai.kr: #e7e9ed */
  background: #fff;                 /* dq.ai.kr: 불투명 순백 */
}
/* 스크롤 전(다크 히어로 위): 로고는 흰 버전 이미지로 교체 */
.logo .logo-white { display: none; }
.site-header:not(.scrolled) .logo .logo-color { display: none; }
.site-header:not(.scrolled) .logo .logo-white { display: block; }
.site-header:not(.scrolled) .link-cta { color: #fff; }
.site-header:not(.scrolled) .link-cta:hover { color: rgba(255,255,255,.72); }
.site-header:not(.scrolled) .link-login { color: rgba(255,255,255,.82); }
.site-header:not(.scrolled) .link-login:hover { color: #fff; }
.site-header:not(.scrolled) .link-guide {
  color: rgba(255,255,255,.9); border-color: rgba(255,255,255,.35); background: transparent;
}
.site-header:not(.scrolled) .link-guide:hover {
  color: #fff; border-color: rgba(255,255,255,.7); background: rgba(255,255,255,.1);
}
/* dq.ai.kr 헤더는 shell 폭 제한 없이 좌우 40px 고정 */
.site-header > .shell {
  display: flex; align-items: center; justify-content: space-between;
  gap: 1.5rem; height: 100%;
  width: 100%; max-width: none; margin-inline: 0; padding-inline: 40px;
}
.logo { display: flex; align-items: center; }
.logo img { width: auto; height: 24px; }
/* 헤더 항목은 셋(소개서·체험하기·로그인). 줄바꿈되면 글자가 세로로 깨집니다 */
.header-actions { display: flex; align-items: center; gap: 30px; white-space: nowrap; }
.link-cta {
  font-size: 15px; font-weight: 700; letter-spacing: -.15px;
  color: ${color.primary}; /* primary */
  transition: color .16s ease;
}
.link-cta:hover { color: var(--brand); } /* dq.ai.kr blue-7 = #1970f4 */
.link-login {
  font-size: 15px; font-weight: 400; letter-spacing: -.15px;
  color: var(--ink-2); /* dq.ai.kr gray-9 = #626975 */
  transition: color .16s ease;
}
.link-login:hover { color: var(--ink); }
/* 소개서 다운로드: 주 CTA(체험하기)와 경쟁하지 않게 테두리만 있는 조용한 버튼 */
.link-guide {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 15px; font-weight: 500; letter-spacing: -.15px;
  color: var(--ink-2); border: 1px solid var(--line); border-radius: 999px;
  padding: 7px 14px;
  transition: color .16s ease, border-color .16s ease, background-color .16s ease;
}
.link-guide:hover { color: var(--brand); border-color: var(--brand); background: var(--brand-tint-2); }
.link-guide svg { transition: transform .16s ease; }
.link-guide:hover svg { transform: translateY(2px); }

/* ── 화면 1: 히어로 (우주 컨셉 · v1.7) ───────────────────────── */
.hero {
  position: relative; overflow: hidden;
  color: #fff;
  padding-top: var(--header-h); /* 고정 헤더가 다크 배경 위에 겹치도록 */
  /* 다크 우주 배경: 딥다크 베이스 + 브랜드 블루 글로우 2겹 */
  background:
    radial-gradient(760px 520px at 74% 26%, rgba(25,112,244,.42) 0%, transparent 62%),
    radial-gradient(620px 460px at 6% 90%, rgba(25,112,244,.24) 0%, transparent 60%),
    ${color.inverse};
}
/* 우주 좌표 격자 (중앙만 은은하게) */
.hero::before {
  content: ""; position: absolute; inset: 0; z-index: 0; pointer-events: none;
  background-image:
    linear-gradient(rgba(255,255,255,.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,.05) 1px, transparent 1px);
  background-size: 54px 54px;
  -webkit-mask-image: radial-gradient(1100px 780px at 55% 42%, #000 0%, transparent 84%);
  mask-image: radial-gradient(1100px 780px at 55% 42%, #000 0%, transparent 84%);
}
/* 장식 레이어 (v1.2: 북극성 하나를 중심으로 · 카피 영역은 비워 둠) */
.space-decor { position: absolute; inset: 0; z-index: 0; pointer-events: none; overflow: hidden; }

/* 북극성: 우측 상단으로 뺀 길잡이별 (v1.7) */
.north-star { position: absolute; left: calc(72% - 66px); top: calc(15% - 66px); width: 132px; height: 132px; animation: floaty 7s ease-in-out infinite; }
.north-star .halo { position: absolute; inset: 0; border-radius: 50%;
  background: radial-gradient(circle, rgba(174,205,255,.50) 0%, rgba(25,112,244,.24) 34%, transparent 68%); }
.north-star svg { position: absolute; left: 50%; top: 50%; width: 60px; height: 60px; margin: -30px 0 0 -30px;
  filter: drop-shadow(0 0 9px rgba(174,205,255,.9)) drop-shadow(0 0 24px rgba(25,112,244,.65)); }
.north-star svg path { animation: twinkle 3.4s ease-in-out infinite; transform-origin: center; }
@keyframes twinkle { 0%,100% { opacity: 1; transform: scale(1); } 50% { opacity: .8; transform: scale(.94); } }

/* 북극성 둘레 궤도 하나만, 아주 은은하게 */
.orbit { position: absolute; border: 1.5px dashed rgba(159,197,255,.20); border-radius: 50%; animation: spin linear infinite; }
.orbit .planet { position: absolute; border-radius: 50%; }
.o1 { width: 380px; height: 380px; left: calc(72% - 190px); top: calc(15% - 190px); animation-duration: 54s; }
.o1 .planet { width: 11px; height: 11px; top: -6px; left: 50%; background: var(--brand); box-shadow: 0 0 12px 2px rgba(25,112,244,.7); }

/* 길잡이 경로: 북극성 → 캐릭터 (아주 은은한 선) */
.guide-path { position: absolute; inset: 0; width: 100%; height: 100%; }
.guide-path line { stroke: rgba(174,205,255,.18); stroke-width: 1.4; }
.guide-path .guide-narrow { display: none; }

/* 먼 별 몇 개 (카피 왼쪽 영역은 피해서 배치) */
.deco { position: absolute; border-radius: 50%; }
.deco.d1 { width: 5px; height: 5px; background: rgba(255,255,255,.75); left: 63%; top: 30%; animation: floaty 5s ease-in-out infinite; }
.deco.d2 { width: 4px; height: 4px; background: rgba(255,255,255,.55); left: 45%; top: 10%; }
.deco.d3 { width: 6px; height: 6px; background: ${color.accent}; left: 58%; bottom: 24%; animation: floaty 6s ease-in-out infinite; }

/* 3D 코딩이 (왼쪽 하단에서 북극성을 올려다보는 구도) */
.hero-char {
  position: absolute; z-index: 1; bottom: 4%; left: 2%;
  width: clamp(140px, 13vw, 195px); pointer-events: none;
  filter: drop-shadow(0 16px 34px rgba(0,0,0,.5));
  animation: floaty 6s ease-in-out infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
@keyframes floaty { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }

/* v1.6: 센터 단일 컬럼 (카피 + 가로 바 폼을 세로로 중앙 배치) */
.hero-grid {
  position: relative; z-index: 2;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  text-align: center; gap: 0;
  min-height: min(calc(100svh - var(--header-h)), 46rem);
  padding-block: 3rem;
}
.hero-copy { max-width: 42rem; margin-inline: auto; }
.hero .eyebrow { background: rgba(255,255,255,.12); color: ${color.primaryLine}; font-size: .8rem; padding: .32rem .8rem; }
/* 카피 = 메인: 센터 배치라 폭이 확보됨 → 크게 */
/* 핵심은 "진로검사는 그대로". 가장 밝은 흰색을 주고, 나머지는 브랜드 블루로 */
.hero h1 {
  margin-top: 1.35rem; color: ${color.primaryLineStrong};
  font-size: clamp(2.3rem, 2.4vw + 1.6vh + .8rem, 4.2rem);
  line-height: 1.2; letter-spacing: -0.035em; font-weight: 800;
  text-shadow: none;
}
/* 핵심 문구는 흰 글자 뒤로 파란 후광을 2겹 깔아 배경에서 떠오르게 */
.hero h1 .accent {
  color: #fff;
  text-shadow:
    0 0 18px rgba(125,176,255,.55),
    0 0 44px rgba(90,150,255,.38),
    0 2px 24px rgba(0,0,0,.4);
}
.hero h1 .accent .plain { color: ${color.primaryLineStrong}; text-shadow: none; }
.hero-body { margin-top: 1.35rem; max-width: 48ch; margin-inline: auto; font-size: 1.02rem; line-height: 1.7; color: rgba(255,255,255,.8); }

/* 히어로 CTA (v1.9: 인라인 폼 → 모달 트리거 버튼) */
.hero-cta { margin-top: 2.4rem; display: flex; flex-direction: column; align-items: center; gap: .9rem; }
.btn-hero {
  border-radius: 12px; background: var(--brand); color: #fff;
  font-weight: 700; font-size: 1rem; padding: .95rem 2.2rem;
  box-shadow: 0 10px 30px rgba(25,112,244,.35);
  transition: background-color .16s ease, transform .16s ease, box-shadow .16s ease;
}
.btn-hero:hover { background: var(--brand-hover); transform: translateY(-1px); box-shadow: 0 14px 34px rgba(25,112,244,.42); }
.btn-hero:active { transform: none; }
.hero-cta-note { font-size: .85rem; line-height: 1.6; color: rgba(255,255,255,.6); }

/* ── 신청 모달 ───────────────────────────────────────────────── */
.modal {
  width: min(100% - 2rem, 34rem);
  max-height: calc(100dvh - 2rem); overflow-y: auto; overscroll-behavior: contain;
  margin: auto; /* 전역 리셋(* { margin: 0 })이 dialog 기본 중앙 정렬을 지워서 되살립니다 */
  padding: 0; border: 0; border-radius: 18px;
  background: var(--surface); color: var(--ink);
  box-shadow: 0 24px 60px rgba(15,23,42,.28);
}
.modal::backdrop { background: rgba(12,18,30,.55); }
.modal.modal-sm { width: min(100% - 2rem, 25rem); }
.modal-card { position: relative; padding: 2rem 1.75rem 1.75rem; }
.modal-close {
  position: absolute; top: .85rem; right: .85rem;
  display: grid; place-items: center; width: 2rem; height: 2rem;
  border-radius: 8px; color: var(--ink-3);
  transition: background-color .16s ease, color .16s ease;
}
.modal-close:hover { background: var(--surface-3); color: var(--ink-2); }
.modal-title { font-size: 1.25rem; font-weight: 800; letter-spacing: -.02em; text-align: center; }
.modal-sub { margin-top: .5rem; text-align: center; font-size: .9rem; line-height: 1.6; color: var(--ink-2); }
/* 참여자 유형 선택 (dq.ai.kr 체험하기 모달과 동일한 구성) */
.play-options { display: flex; flex-direction: column; gap: .6rem; margin-top: 1.35rem; }
.play-option {
  display: flex; flex-direction: column; gap: .2rem;
  border: 1px solid var(--line); border-radius: 12px; padding: 1rem 1.15rem;
  transition: border-color .16s ease, background-color .16s ease;
}
.play-option:hover { border-color: var(--brand); background: var(--brand-tint-2); }
.play-option-title { font-size: .98rem; font-weight: 700; color: var(--ink); }
.play-option-desc { font-size: .86rem; color: var(--ink-2); }
/* 요원 유형 선택 개편판(?new=1) — "코딩이가 사라졌다" 포스터형 다크 모달. 기존 .play-* 와 겹치지 않게 .modal-play 아래로만 */
.modal.modal-play {
  width: min(100% - 2rem, 350px);
  background: ${color.inverse}; color: ${color.white};
}
.modal-play .modal-card { padding: 0; }
.modal-play .modal-close { color: rgba(255,255,255,.75); z-index: 1; }
.modal-play .modal-close:hover { background: rgba(255,255,255,.12); color: ${color.white}; }
.modal-play .play-head { padding: 1.7rem 1.25rem .9rem; text-align: center; }
.modal-play .play-title {
  font-size: clamp(1.45rem, 6vw, 1.7rem); font-weight: 800; line-height: 1.2; letter-spacing: -.02em;
  color: ${color.white};
}
.modal-play .play-title-sub { display: block; }
.modal-play .play-signal-line { margin-top: .55rem; font-size: .95rem; font-weight: 700; color: rgba(255,255,255,.85); }
.modal-play .play-signal { color: ${color.accent}; }
/* PC 에서는 포스터(250px)와 유형 버튼(290px)을 가운데 세운다. 모바일 풀스크린은 화면 폭. */
.modal-play .play-poster { display: block; width: 250px; height: auto; margin: 0 auto; border-radius: 12px; }
.modal-play .play-foot { width: 290px; margin: 0 auto; padding: .9rem 0 1.2rem; }
.modal-play .play-choose { text-align: center; font-size: .8rem; color: ${color.primaryLineStrong}; margin-bottom: .5rem; }
.modal-play .play-options { display: flex; flex-direction: column; gap: .5rem; margin-top: 0; }
.modal-play .play-option {
  /* 기존 .play-option 이 세로(column)라 가로로 되돌린다 */
  display: flex; flex-direction: row; align-items: center; justify-content: space-between; gap: .75rem;
  height: 58px; border-radius: 12px; padding: 0 1rem 0 1.1rem;
  color: ${color.white}; border: 1px solid transparent;
  transition: background-color .16s ease;
}
.modal-play .play-option-primary { background: ${color.primary}; }
.modal-play .play-option-primary:hover { background: ${color.primaryHover}; }
.modal-play .play-option-secondary { background: rgba(255,255,255,.06); border-color: ${color.primaryLineStrong}80; }
.modal-play .play-option-secondary:hover { background: rgba(255,255,255,.12); }
.modal-play .play-option:focus-visible { outline: 2px solid ${color.primaryLineStrong}; outline-offset: 2px; }
.modal-play .play-option-text { display: flex; flex-direction: column; gap: .15rem; min-width: 0; }
.modal-play .play-option-title { font-size: .95rem; font-weight: 700; color: ${color.white}; }
.modal-play .play-option-desc { font-size: .78rem; color: ${color.primaryLine}; }
.modal-play .play-option-secondary .play-option-desc, .modal-play .play-option-secondary svg { color: ${color.primaryLineStrong}; }
.modal-play .play-option svg { flex-shrink: 0; }

/* 입력 항목은 좌·우 2열, 문의사항만 한 줄 전체 */
.modal-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem .75rem; margin-top: 1.35rem; }
.modal-field { min-width: 0; border: 0; padding: 0; }
.modal-field.full { grid-column: 1 / -1; }
.modal-field > label {
  display: flex; align-items: baseline; gap: .3rem;
  margin-bottom: .4rem; font-size: .85rem; font-weight: 600; color: var(--ink);
}
.modal-field .req { color: var(--brand); }
.modal-field .opt { font-weight: 400; color: var(--ink-3); }
.modal-field .counter { margin-left: auto; font-size: .75rem; font-weight: 400; color: var(--ink-3); font-variant-numeric: tabular-nums; }
.modal-field input,
.modal-field select,
.modal-field textarea {
  width: 100%; border: 1px solid var(--line); border-radius: 10px;
  background: var(--surface); color: var(--ink);
  padding: .72rem .85rem; font-size: .94rem;
  transition: border-color .16s ease, box-shadow .16s ease;
}
.modal-field textarea { resize: vertical; min-height: 5.2rem; line-height: 1.6; }
.modal-field input::placeholder,
.modal-field textarea::placeholder { color: var(--ink-3); }
.modal-field input:focus,
.modal-field select:focus,
.modal-field textarea:focus {
  outline: none; border-color: var(--brand); box-shadow: 0 0 0 3px var(--brand-tint);
}
.modal-field select {
  appearance: none; cursor: pointer;
  padding-right: 2.1rem;
  /* ★ data URI 안에 '//' 를 그대로 쓰면 안 된다. SWC 압축기가 주석으로 오인해
     이 지점부터 줄 끝까지 지워, 따옴표가 안 닫힌 채 남고 이후 CSS 전체가 무효가 된다.
     반드시 %2F%2F 로 인코딩할 것 (xmlns 의 http%3A%2F%2F 부분) */
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg' width='12' height='8' viewBox='0 0 12 8' fill='none'%3E%3Cpath d='m1 1.5 5 5 5-5' stroke='%239aa1ac' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat; background-position: right .85rem center;
}
.modal-field select:invalid { color: var(--ink-3); } /* 미선택 안내 문구는 placeholder 톤으로 */
.modal-field select:disabled { background-color: var(--surface-2); cursor: not-allowed; }
.consent {
  display: flex; align-items: flex-start; gap: .5rem;
  margin-top: 1.1rem; font-size: .85rem; line-height: 1.5; color: var(--ink-2);
}
.consent input { width: 1em; height: 1em; margin-top: .18em; flex-shrink: 0; accent-color: var(--brand); }
.consent a { color: var(--brand); text-decoration: underline; text-underline-offset: 2px; }
.form-note { margin-top: .8rem; text-align: center; font-size: .8rem; line-height: 1.6; color: var(--ink-3); }
.form-note.is-error { color: ${color.error}; }
.modal-body button[type=submit] {
  width: 100%; margin-top: 1rem; border-radius: 12px;
  background: var(--brand); color: #fff; font-weight: 700; font-size: .98rem;
  padding: .88rem 1.2rem; transition: background-color .16s ease;
}
.modal-body button[type=submit]:hover { background: var(--brand-hover); }
.modal-body button[type=submit]:disabled { opacity: .55; cursor: default; }
/* 미입력 상태: 비활성처럼 보이되 클릭은 받아 어떤 항목이 남았는지 알려줍니다 */
.modal-body button[type=submit].is-incomplete,
.modal-body button[type=submit].is-incomplete:hover {
  background: var(--surface-3); color: var(--ink-3);
}

.modal-done { display: none; flex-direction: column; align-items: center; gap: .55rem; padding: 1.6rem 0 .4rem; text-align: center; }
.modal-done .t-body { color: var(--ink-2); }
.modal-card.is-done .modal-body { display: none; }
.modal-card.is-done .modal-done { display: flex; }
.modal-done .check {
  display: grid; place-items: center; width: 2.8rem; height: 2.8rem;
  border-radius: 999px; background: var(--brand-tint); color: var(--brand);
}
.modal-done-btn {
  width: 100%; margin-top: 1rem; border-radius: 12px;
  background: var(--brand); color: #fff; font-weight: 700; font-size: .98rem;
  padding: .88rem 1.2rem; transition: background-color .16s ease;
}
.modal-done-btn:hover { background: var(--brand-hover); }

/* ── 화면 2: 해봄이란? (landing2 v2.1 지그재그 피처 행) ─────────── */
.sec2-head { max-width: 46rem; margin: 0 auto; text-align: center; }
.sec2-head .t-body { margin-top: 1.1rem; }
/* .reverse 는 별도 규칙 없이 DOM 순서(이미지 먼저)로 좌우가 뒤집힙니다 */
.feature-row {
  display: grid; grid-template-columns: 1fr 1fr; gap: 3.5rem; align-items: start;
  margin-top: 4.5rem;
}
.feature-text h3 {
  font-size: clamp(1.35rem, .8vw + 1.1rem, 1.9rem);
  font-weight: 700; letter-spacing: -0.02em;
}
/* "A가 아니라, B" 에서 B 만 브랜드 컬러로 */
.feature-text h3 .hl { color: var(--brand); }
.feature-text > p { margin-top: 1rem; font-size: 1.02rem; line-height: 1.75; color: var(--ink-2); }
.feature-text b { color: var(--ink); font-weight: 700; }
.feature-points { display: flex; flex-direction: column; gap: .6rem; margin-top: 1.3rem; }
.feature-points li { position: relative; padding-left: 1.4rem; font-size: .96rem; line-height: 1.6; color: var(--ink); }
.feature-points li::before {
  content: ""; position: absolute; left: 0; top: .55em;
  width: .5rem; height: .5rem; border-radius: 999px; background: var(--brand);
}
.feature-visual img {
  width: 100%; display: block; border-radius: 16px;
  border: 1px solid var(--line);
  box-shadow: 0 24px 56px -28px rgba(31,33,35,.35);
}
@media (max-width: 860px) {
  .feature-row { grid-template-columns: 1fr; gap: 1.5rem; margin-top: 3rem; }
  /* 좌우 반전 행도 좁은 화면에선 글 → 이미지 순서로 통일 */
  .feature-text { order: 1; }
  .feature-visual { order: 2; }
}

/* ── 화면 3: 사업 실증 (landing2 v3.0. 지표·사례·인터뷰·인증 도장) ── */
.proof { background: var(--surface-2); }
.sec3-head { max-width: 46rem; margin: 0 auto; text-align: center; }
.sec3-head .t-h2 { margin-top: 0; }

/* 지표 카드: 오른쪽 인터뷰 칸 위, 2개 나란히 콤팩트 */
/* margin-bottom 은 인용문과의 최소 간격입니다. 실제로는 여기에 남는 공간이 더 붙습니다
   (.interview-quote 의 margin-top: auto 참고) */
.stat-cards { display: grid; grid-template-columns: 1fr 1fr; gap: .75rem; margin-bottom: 1.25rem; }
.stat-card { border: 1px solid var(--line); border-radius: 16px; background: #fff; padding: 1.1rem 1rem; text-align: center; }
.stat-card dd {
  font-size: 1.75rem; line-height: 1; font-weight: 800;
  color: var(--brand); letter-spacing: -0.03em; font-variant-numeric: tabular-nums;
}
.stat-card dd .plus, .stat-card dd .of { font-size: .5em; font-weight: 700; margin-left: .05em; }
.stat-card dd .of { color: var(--ink-3); }
.stat-card dt { margin-top: .7rem; font-size: .88rem; color: var(--ink-2); }

.proof-grid { display: grid; grid-template-columns: 1.15fr .85fr; gap: 2.5rem; margin-top: 2.75rem; align-items: start; }
.block-title { font-size: .95rem; font-weight: 700; color: var(--ink); }
.case-list { display: flex; flex-direction: column; margin-top: 1.1rem; }
.case-list li { display: flex; gap: 1rem; align-items: flex-start; padding: .95rem 0; border-bottom: 1px solid var(--line); }
.case-list li:last-child { border-bottom: 0; }
.case-list .yr {
  flex: none; width: 2.9rem; text-align: center; padding: .35rem 0; border-radius: 10px;
  background: var(--brand); color: #fff; font-size: .82rem; font-weight: 700;
}
.case-list b { font-size: .98rem; }
.case-list li p { margin-top: .2rem; font-size: .88rem; line-height: 1.55; color: var(--ink-2); }

/* 왼쪽 '주요 도입 사례' 제목 한 줄만큼 내려, 지표 카드가 첫 사례(홍대부속초등학교)와
   같은 선에서 시작하게 합니다. = block-title 높이(.95rem × line-height 1.5) + case-list 상단 여백 */
.interview {
  margin-top: calc(.95rem * 1.5 + 1.1rem);
  /* 칼럼을 왼쪽 높이에 맞춰 늘리고(stretch), 남는 공간은 아래 인용문 위로 몰아
     영상 하단이 왼쪽 인증 도장 하단과 같은 선에 오게 합니다. 폭이 바뀌어도 따라옵니다. */
  align-self: stretch;
  display: flex; flex-direction: column;
}
.interview-quote {
  margin-top: auto;
  font-size: .96rem; line-height: 1.7; color: var(--ink); font-style: italic;
  padding-left: .9rem; border-left: 3px solid var(--brand-tint);
}

/* 인증 원형 도장 (사례 아래) */
.seal-row { display: flex; gap: 1.25rem; margin-top: 2rem; flex-wrap: wrap; }
.seal-item { display: flex; flex-direction: column; align-items: center; text-align: center; width: 158px; }
.seal {
  position: relative; width: 156px; height: 156px; border-radius: 999px; background: ${scale.indigo[800]}; color: #fff;
  display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 16px;
  box-shadow: 0 12px 26px -14px rgba(23,51,95,.7);
}
.seal::before { content: ""; position: absolute; inset: 8px; border-radius: 999px; border: 1.5px dashed rgba(230,199,138,.55); }
.seal .s-star { margin-bottom: 2px; }
.seal .s-top { font-size: 10px; font-weight: 700; letter-spacing: .03em; color: ${scale.amber[300]}; }
.seal .s-mid { font-size: 14.5px; font-weight: 800; line-height: 1.15; margin: 2px 0; word-break: keep-all; }
.seal .s-bot { font-size: 9px; line-height: 1.2; color: rgba(255,255,255,.82); word-break: keep-all; }
/* 예전엔 도장 밖 캡션(.seal-cap)이었는데 v3.4 에서 도장 안 네 번째 줄로 들어왔습니다 */
.seal .s-sub { margin-top: 3px; font-size: 9px; line-height: 1.2; color: rgba(230,199,138,.9); word-break: keep-all; }

/* 인터뷰 영상: 썸네일을 먼저 두고 클릭할 때만 iframe 으로 교체 */
.interview-video { position: relative; margin-top: 1.1rem; border-radius: 14px; overflow: hidden; border: 1px solid var(--line); box-shadow: 0 18px 40px -24px rgba(31,33,35,.3); aspect-ratio: 16 / 9; background: #000; }
.iv-facade { display: block; width: 100%; height: 100%; padding: 0; border: 0; background: none; cursor: pointer; position: relative; }
.iv-facade img { width: 100%; height: 100%; object-fit: cover; display: block; }
.iv-play { position: absolute; inset: 0; margin: auto; width: 3.5rem; height: 3.5rem; display: grid; place-items: center; border-radius: 999px; background: rgba(25,112,244,.92); box-shadow: 0 6px 20px rgba(0,0,0,.35); transition: transform .16s ease; }
.iv-facade:hover .iv-play { transform: scale(1.08); }
.interview-video iframe { width: 100%; height: 100%; border: 0; display: block; }

@media (max-width: 860px) {
  .stat-cards { grid-template-columns: 1fr; justify-content: stretch; }
  .proof-grid { grid-template-columns: 1fr; gap: 2rem; }
  .seal-row { justify-content: center; }
  /* 1열에서는 위아래로 쌓이므로 좌측 제목에 맞춘 오프셋이 필요 없습니다 */
  .interview { margin-top: 0; }
}

/* 푸터는 운영 사이트와 같은 앱 푸터(layout/Footer, Tailwind)를 그대로 쓰므로
   랜딩 전용 푸터 CSS 는 두지 않습니다. */

/* ── 반응형 ──────────────────────────────────────────────────── */
@media (max-width: 1023px) {
  /* 코딩이(이미지 비율 0.875)가 설 자리를 히어로 하단에 확보합니다 */
  .hero-grid {
    grid-template-columns: 1fr; gap: 2.75rem; min-height: 0;
    padding-top: 3.5rem;
    padding-bottom: calc(3.5rem + clamp(112px, 30vw, 168px) * 0.875);
  }
  .hero-copy { max-width: none; }
  /* 궤도만 숨기고 캐릭터는 하단 중앙 아래에 유지 */
  .orbit { display: none; }
  /* 세로로 긴 화면에서는 안내선이 거의 수직으로 서서 코딩이가 엉뚱한 곳을
     가리키는 모양이 됩니다. 북극성을 오른쪽으로 밀어 각도를 눕힙니다. */
  .north-star { left: calc(84% - 66px); }
  .guide-path .guide-wide { display: none; }
  .guide-path .guide-narrow { display: block; }
  /* 북극성으로 이어지는 시선 유도선 끝에 맞춰 좌측 하단 구도를 유지 */
  .hero-char { width: clamp(112px, 30vw, 168px); bottom: 1.5rem; left: 4%; }
}
@media (max-width: 767px) {
  .btn-hero { width: 100%; }
  /* 좁은 화면에선 헤더 좌우 여백만 축소 */
  .site-header > .shell { padding-inline: 20px; }
  .header-actions { gap: 18px; }
  /* 글자 크기는 그대로 두고 손가락이 닿는 영역만 넓힙니다 */
  .link-cta, .link-login { padding-block: .6rem; }
  /* 헤더 항목이 셋이라 좁은 화면에선 빡빡합니다. 소개서 버튼만 줄여 자리를 냅니다 */
  .link-guide { font-size: 14px; padding: 6px 10px; gap: 4px; }
  .link-guide .guide-long { display: none; }
  .header-actions { gap: 14px; }
}
/* 더 좁아지면 소개서는 아이콘만 남깁니다 (이름은 aria-label 이 유지) */
@media (max-width: 560px) {
  .link-guide .guide-text { display: none; }
  .link-guide { padding: 7px; }

  /* 모달: 화면 하단에 붙는 시트 형태 + 입력은 1열 */
  .modal {
    width: 100%; max-width: none; max-height: 92dvh;
    margin: auto auto 0; border-radius: 18px 18px 0 0;
  }
  .modal-card { padding: 1.6rem 1.15rem calc(1.4rem + env(safe-area-inset-bottom)); }
  .modal-grid { grid-template-columns: 1fr; gap: .9rem; margin-top: 1.2rem; }
  .modal-title { font-size: 1.15rem; }
  /* iOS 는 16px 미만 입력에서 화면을 확대해버립니다 */
  .modal-field input,
  .modal-field select,
  .modal-field textarea { font-size: 16px; }

  /* 요원 유형 모달: 모바일에서는 풀스크린(포스터가 화면을 채운다) */
  .modal.modal-play {
    width: 100vw; max-width: none; height: 100dvh; max-height: none;
    margin: 0; border-radius: 0;
  }
  .modal-play .modal-card {
    padding: 0; min-height: 100dvh; display: flex; flex-direction: column;
  }
  .modal-play .play-head { padding-top: calc(2.2rem + env(safe-area-inset-top)); }
  .modal-play .play-title { font-size: clamp(1.6rem, 7vw, 2rem); }
  .modal-play .play-poster { width: 100%; border-radius: 0; }
  .modal-play .play-foot { width: auto; padding-left: 1.25rem; padding-right: 1.25rem; }
  .modal-play .play-option { height: 64px; }
  .modal-play .play-foot {
    flex-grow: 1; display: flex; flex-direction: column; justify-content: flex-end;
    padding-bottom: calc(1.5rem + env(safe-area-inset-bottom));
  }
}

@media (prefers-reduced-motion: reduce) {
  html { scroll-behavior: auto; }
  *, *::before, *::after {
    animation-duration: .01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: .01ms !important;
  }
}
    `}</style>
  );
}
